'use client';

import { useMemo } from 'react';

import { SPECIAL_METHOD } from '@/constants';
import { CartLineItem } from '@/types';
import {
  resolveCartBadgeDealContext,
  resolveCartBadgeDealIds,
} from '@/utils/bogoSpecialCart';
import { useCartDealStatesContext } from '../../context/cartDealStates';

import { DealBundleState } from './bundleProgress';

export type CartLineDealBadge = {
  showBadge: boolean;
  isBundleOrBogoSpecial: boolean;
  badgeMeta?: { discount_id: string; discount_title: string };
};

/** Cart line badge — read-only, multi-deal aware; never mutates the line. */
const useCartLineDealBadge = (product: CartLineItem): CartLineDealBadge => {
  const dealIds = useMemo(
    () => resolveCartBadgeDealIds(product),
    [product.bundle_id, product.discounts],
  );

  const { getDealState, isLoading } = useCartDealStatesContext();

  return useMemo(() => {
    const emptyResult: CartLineDealBadge = {
      showBadge: false,
      isBundleOrBogoSpecial: false,
    };

    const dealStates = dealIds
      ?.map(id => getDealState(id))
      ?.filter((state): state is DealBundleState => Boolean(state));

    if (isLoading || !dealIds.length || !dealStates.length) {
      return emptyResult;
    }

    const { showBadge, badgeDealId } = resolveCartBadgeDealContext(
      dealStates.map(state => ({
        dealId: state.specialId,
        bundleComplete: state.bundleComplete,
      })),
    );

    const badgeDeal = dealStates.find(state => state.specialId === badgeDealId);

    if (!showBadge || !badgeDeal) {
      return emptyResult;
    }

    const isBundleOrBogoSpecial = [
      SPECIAL_METHOD.BOGO,
      SPECIAL_METHOD.BUNDLE,
    ].includes(badgeDeal.method as SPECIAL_METHOD);

    if (!isBundleOrBogoSpecial) {
      return emptyResult;
    }

    return {
      showBadge: true,
      isBundleOrBogoSpecial,
      badgeMeta: {
        discount_id: badgeDeal.specialId,
        discount_title: badgeDeal.title,
      },
    };
  }, [dealIds, getDealState, isLoading]);
};

export { useCartLineDealBadge };
